import { combineReducers } from "redux";
import $ from "jquery";

import * as filterActions from "./actions/filters";
import * as loadingActions from "./actions/loading";
import * as dhsa from "./actions/data_hub_search";
import * as navigationActions from "./actions/navigation";
import { AJAX } from "./actions/ajax";

import { assignSelect, resetSelect, assignMultiSelect, resetMultiSelect, loadSelect, loadMultiSelect } from "../../shared/select_and_multi_select_helpers";

const makeSelectReducer = (changeAction, generateStateFromChange, resetState, generateStateFromLoad) => {
  return (state = { options: [], selectedValues: [], defaultSelectedValues: [], notUsed: true }, action) => {
    switch(action.type) {
    case changeAction:
      return generateStateFromChange(state, action);
    case loadingActions.DATA_LOADED:
      if (action.triggeringType === dhsa.SAVED_SEARCH_SELECTED && !state.notUsed) {
        return generateStateFromLoad(state, action);
      } else {
        return state;
      }
    case filterActions.RESET_ALL:
      return resetState(state, action);
    default:
      return state;
    }
  };
};

const toggleValue = (selectedValues, clickedOption) => {
  let newList;

  if (clickedOption.addOptions) {
    const existingValues = selectedValues.map(e => e.value);
    const newValues = clickedOption.addOptions.map(e => e.value);
    const set = new Set(existingValues.concat(newValues));

    const combined = clickedOption.addOptions.concat(selectedValues);

    newList = [...set].map(val => combined.find(e => val === e.value));
  } else if (clickedOption.removeOptions) {
    // const existingValues = selectedValues.map(e => e.value);
    const removeValues = clickedOption.removeOptions.map(e => e.value);

    newList = selectedValues.filter(e => !removeValues.find(f => f === e.value));
  } else if (selectedValues.find(e => e.value === clickedOption.value)) {
    // Unchecked so remove from list
    newList = selectedValues.filter(f => f.value !== clickedOption.value);
  } else {
    // chcked so add to list
    newList = selectedValues.concat(clickedOption);
  }

  return newList;
};

const makeMainReducer = (focusView) => {
  const purposeBase = makeSelectReducer(filterActions.PURPOSE_CHANGED, assignSelect, resetSelect, loadSelect("purpose"));
  const purpose = (state, action) => {
    switch(action.type) {
    case filterActions.CONNECTED_PURPOSE_CHANGED:
      return assignSelect(state, action);
    default:
      return purposeBase(state, action);
    }
  };

  const purposes = makeSelectReducer(filterActions.PURPOSES_CHANGED, assignMultiSelect, resetMultiSelect, loadMultiSelect("purposes"));

  const regionsBase = makeSelectReducer(filterActions.REGIONS_CHANGED, assignMultiSelect, resetMultiSelect, loadMultiSelect("regions"));

  const regions = (state, action) => {
    switch(action.type) {
    case filterActions.CONNECTED_COUNTRIES_CHANGED:
    case filterActions.CONNECTED_PARAMETERS_CHANGED:
    case filterActions.CONNECTED_PURPOSE_CHANGED:
      return resetMultiSelect(state, action);
    default:
      return regionsBase(state, action);
    }
  };

  const subPurpose = makeSelectReducer(filterActions.SUB_PURPOSE_CHANGED, assignSelect, resetSelect, loadSelect("subPurpose"));
  const subPurposes = makeSelectReducer(filterActions.SUB_PURPOSES_CHANGED, assignMultiSelect, resetMultiSelect, loadMultiSelect("subPurposes"));

  const countriesBase = makeSelectReducer(filterActions.COUNTRIES_CHANGED, assignMultiSelect, resetMultiSelect, loadMultiSelect("countries"));
  const countries = (state, action) => {
    switch(action.type) {
    case filterActions.CONNECTED_COUNTRIES_CHANGED:
      return assignMultiSelect(state, action);
    case filterActions.CONNECTED_PARAMETERS_CHANGED:
    case filterActions.CONNECTED_PURPOSE_CHANGED:
      return resetMultiSelect(state, action);
    default:
      return countriesBase(state, action);
    }
  };

  // makeSelectReducer(filterActions.PARAMETERS_CHANGED, assignMultiSelect, resetMultiSelect, loadMultiSelect("parameters"));
  const parameters = (state = { options: [], selectedValues: [], defaultSelectedValues: [], notUsed: true }, action) => {
    switch(action.type) {
    case filterActions.CONNECTED_PARAMETERS_CHANGED:
    case filterActions.PARAMETERS_CHANGED:
      return (Object.assign({}, state, { selectedValues: toggleValue(state.selectedValues, action.payload) }));
    case loadingActions.DATA_LOADED:
      if (action.triggeringType === dhsa.SAVED_SEARCH_SELECTED && !state.notUsed) {
        return loadMultiSelect("parameters")(state, action);
      } else {
        return state;
      }
    case filterActions.CONNECTED_PURPOSE_CHANGED:
    case filterActions.RESET_ALL:
      return resetMultiSelect(state, action);
    default:
      return state;
    }
  };

  const regulations = makeSelectReducer(filterActions.REGULATIONS_CHANGED, assignMultiSelect, resetMultiSelect, loadMultiSelect("regulations"));

  const minMax = makeSelectReducer(filterActions.MIN_MAX_CHANGED, assignMultiSelect, resetMultiSelect, loadMultiSelect("minMax"));

  const regulationName = (state = "", action) => {
    switch(action.type) {
    case filterActions.REGULATION_NAME_CHANGED:
      return action.payload;
    case filterActions.REGULATIONS_CHANGED:
    case filterActions.RESET_ALL:
      return "";
    case loadingActions.DATA_LOADED:
      if (action.triggeringType === dhsa.SAVED_SEARCH_SELECTED) {
        return action.payload.filters.regulationName;
      } else {
        return state;
      }
    default:
      return state;
    }
  };

  const showAdvancedSearch = (state = false, action) => {
    switch(action.type) {
    case navigationActions.TOGGLE_ADVANCED_SEARCH:
      return !state;
    case loadingActions.DATA_LOADED:
      if (action.triggeringType === dhsa.SAVED_SEARCH_SELECTED) {
        return action.payload.filters.showAdvancedSearch;
      } else {
        return state;
      }
    case filterActions.RESET_ALL:
      return false;
    default:
      return state;
    }
  };

  const makeBooleanFilter = (clickedAction, filterKey) => (state = "unchecked", action) => {
    switch(action.type) {
    case clickedAction:
      return state === "unchecked" ? "checked" : "unchecked";
    case loadingActions.DATA_LOADED:
      if (action.triggeringType === dhsa.SAVED_SEARCH_SELECTED) {
        return action.payload.filters[filterKey];
      } else {
        return state;
      }
    case filterActions.RESET_ALL:
      return "unchecked";
    default:
      return state;
    }
  };

  const showOnlyRegulationsWithNumericalStandards = makeBooleanFilter(filterActions.SHOW_ONLY_REGULATIONS_WITH_NUMERICAL_STANDARDS_CLICKED, "showOnlyRegulationsWithNumericalStandards");

  const showOnlyMandatory = makeBooleanFilter(filterActions.SHOW_ONLY_MANDATORY_CLICKED, "showOnlyMandatory");

  const showOnlyCurrent = makeBooleanFilter(filterActions.SHOW_ONLY_CURRENT_CLICKED, "showOnlyCurrent");

  const showParametersPanel = (state = false, action) => {
    switch(action.type) {
    case filterActions.EXPAND_PARAMETER_PANEL:
      return true;
    case filterActions.COLLAPSE_PARAMETER_PANEL:
      return false;
    default:
      return state;
    }
  };

  const makeTableOptions = (key, pageChangeType, sortedChangeType, expandedChangeType) => {
    return (state = {}, action) => {
      switch(action.type) {
      case loadingActions.DATA_LOADED:
        if (action.triggeringType === dhsa.SAVED_SEARCH_SELECTED) {
          return action.payload.filters.tables[key];
        } else {
          return state;
        }
      case pageChangeType:
        return Object.assign({}, state, { page: action.payload, expanded: [] });
      case sortedChangeType:
        return Object.assign({}, state, { sorted: action.payload, page: 0, expanded: [] });
      case expandedChangeType:
        return Object.assign({}, state, { expanded: action.payload });
      default:
        return state;
      }
    };
  };

  const focusRegulationId = (state = {}, action) => {
    switch(action.type) {
    case filterActions.FOCUS_REGULATION_ID:
      return { id: action.payload };
    case loadingActions.DATA_LOADED:
      if (action.triggeringType === dhsa.SAVED_SEARCH_SELECTED) {
        return { id: (action.payload.filters.focusRegulationId || {}).id };
      } else {
        return state;
      }
    default:
      return state;
    }
  };

  const focusParameterId = (state = {}, action) => {
    switch(action.type) {
    case filterActions.FOCUS_PARAMETER_ID:
      return { id: action.payload };
    case filterActions.RESET_PARAMETER_AND_COUNTRY_ID:
      return {};
    case loadingActions.DATA_LOADED:
      if (action.triggeringType === dhsa.SAVED_SEARCH_SELECTED) {
        return { id: (action.payload.filters.focusParameterId || {}).id };
      } else {
        return state;
      }
    case filterActions.FOCUS_PARAMETER_AND_COUNTRY_ID:
      return { id: action.payload.parameterId };
    default:
      return state;
    }
  };

  const focusCountryId = (state = {}, action) => {
    switch(action.type) {
    case filterActions.FOCUS_PARAMETER_ID:
      return { id: action.payload };
    case filterActions.RESET_PARAMETER_AND_COUNTRY_ID:
      return {};
    case loadingActions.DATA_LOADED:
      if (action.triggeringType === dhsa.SAVED_SEARCH_SELECTED) {
        return { id: (action.payload.filters.focusParameterId || {}).id };
      } else {
        return state;
      }
    case filterActions.FOCUS_PARAMETER_AND_COUNTRY_ID:
      return { id: action.payload.countryId };
    default:
      return state;
    }
  };

  const mainRegulations = makeTableOptions("mainRegulations", filterActions.TABLE_MAIN_REGULATIONS_PAGE, filterActions.TABLE_MAIN_REGULATIONS_SORTED, filterActions.TABLE_MAIN_REGULATIONS_EXPANDED);

  const mainRegulationStandards = makeTableOptions("mainRegulationStandards", filterActions.TABLE_MAIN_STANDARDS_PAGE, filterActions.TABLE_MAIN_STANDARDS_SORTED, filterActions.TABLE_MAIN_STANDARDS_EXPANDED);

  const tables = combineReducers({
    mainRegulations, mainRegulationStandards
  });

  const filters = combineReducers({
    purpose, purposes, countries, parameters, regulations,
    subPurpose, subPurposes, showAdvancedSearch, showParametersPanel,
    tables, focusRegulationId, focusParameterId, focusCountryId, regulationName,
    showOnlyRegulationsWithNumericalStandards,
    minMax,
    showOnlyMandatory, showOnlyCurrent,
    regions
  });

  const loading = (state = {}, action) => {
    switch(action.type) {
    case loadingActions.LOADING_DATA_STARTED:
      return Object.assign({}, state, { showLoadingOverlay: true });
    case loadingActions.DATA_LOADED:
      return Object.assign({}, state, action.payload.loading);
    case loadingActions.LOADING_DATA_FINISHED:
      return Object.assign({}, state, { savedSearchLoading: false, showLoadingOverlay: false });
    case dhsa.SAVED_SEARCH_SELECTED:
      return Object.assign({}, state, { savedSearchLoading: true });
    default:
      return state;
    }
  };

  const path = (state = "") => {
    return state;
  };

  const navigation = combineReducers({focusView, path});

  const newSavedSearchOption = {
    label: "New saved search",
    value: dhsa.NEW_VALUE
  };
  const defaultSavedSearchState = {
    searchIsBeingSaved: false,
    selectedOptionValue: dhsa.NEW_VALUE,
    textValue: "",
    newOption: newSavedSearchOption,
    options: [],
    loadingOptions: true,
    searchIsBeingSubmitted: false,
    chartSvg: false
  };

  const savedSearches = (state = defaultSavedSearchState, action) => {
    switch(action.type) {
    case dhsa.SAVED_SEARCH_OPTIONS_TRIGGER:
      return Object.assign({}, state, { searchIsBeingSaved: true });
    case dhsa.DOWNLOAD_SAVED_SEARCH_OPTIONS_TRIGGER:
      return Object.assign({}, state, { searchIsBeingSaved: true, downloading: true });
    case dhsa.SAVED_SEARCH_SUBMIT_TRIGGER:
      return Object.assign({}, state, { downloading: state.downloading || !!action.payload });
    case dhsa.SAVED_SEARCH_SUBMIT_TEXT_CHANGE:
      return Object.assign({}, state, { textValue: action.payload });
    case dhsa.SAVED_SEARCH_SUBMIT_RADIO_BUTTON_CLICKED:
      return Object.assign({}, state, {
        textValue: "",
        selectedOptionValue: action.payload === dhsa.NEW_VALUE ? action.payload : +action.payload
      });
    case dhsa.SAVED_SEARCH_OPTIONS_RESPONSE_RECEIVED:
      return Object.assign({}, state, { options: action.payload, loadingOptions: false });
    case dhsa.SAVED_SEARCH_SUBMIT_START:
      return Object.assign({}, state, { searchIsBeingSubmitted: true });
    case dhsa.SAVED_SEARCH_OPTIONS_START:
    case dhsa.SAVED_SEARCH_OPTIONS_FINISHED:
    case dhsa.SAVED_SEARCH_SUBMIT_RESPONSE_RECEIVED:
    case AJAX:
      return state;
    case dhsa.SAVED_SEARCH_SUBMIT_FINISHED:
      if (state.downloading) {
        $("#downloadingModal").modal("show");
      }
      return defaultSavedSearchState;
    case dhsa.SAVED_SEARCH_SUBMIT_CANCEL:
    case filterActions.RESET_ALL:
    default:
      return defaultSavedSearchState;
    }
  };

  const dataHubSearch = (state = {}, action) => {
    if (Object.keys(filterActions).includes(action.type)) {
      return Object.assign({}, state, { selectedSavedSearch: null });
    }

    switch(action.type) {
    case dhsa.SAVED_SEARCH_SELECTED:
      return Object.assign({}, state, { selectedSavedSearch: action.payload });
    case dhsa.SAVED_SEARCH_OPTIONS_RESPONSE_RECEIVED:
      return Object.assign({}, state, { savedSearchOptions: action.payload });
    case dhsa.SAVED_SEARCH_SUBMIT_RESPONSE_RECEIVED:
      return Object.assign({}, state, action.payload.dataHubSearch);
    default:
      return state;
    }
  };

  const countryParameterPurposeMap = (state = {}) => {
    return state;
  };

  const mainReducer = combineReducers({
    filters, loading, navigation, dataHubSearch, savedSearches, countryParameterPurposeMap
  });

  return mainReducer;
};

export default makeMainReducer;
